import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { API } from '../api';
import { formatDate } from '../utils/dateUtils';
import Pagination from './Pagination';
import AudioDurationCard from './AudioDurationCard';
import { formatGenerationStatus, formatNullableMoney } from '../utils/adminFormatters';
import './SummariesTab.css';
import { useTheme } from '../context/ThemeContext';
import { getChartTheme } from '../utils/theme';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const SummariesTab = ({ stats, audioDuration, loading, onUserClick }) => {
  const [chartData, setChartData] = useState(null);
  const [summaries, setSummaries] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 5;
  const lineChartRef = useRef(null);
  const lineChartInstanceRef = useRef(null);
  const { theme } = useTheme();
  const chartColorsRef = useRef(getChartTheme(theme));

  const normalizeSummary = (s) => {
    const user = s.owner || {};
    let reaction = s.userReaction || s.user_reaction || s.reaction || 'none';

    if (s.reactions) {
      if (s.reactions.like > 0) reaction = 'like';
      else if (s.reactions.dislike > 0) reaction = 'dislike';
    }

    let viewsTotal = 0;
    let viewsToday = 0;

    if (s.views && typeof s.views === 'object') {
      viewsTotal = s.views.total || 0;
      viewsToday = s.views.today || 0;
    } else {
      viewsTotal = s.views || s.view_count || 0;
    }

    return {
      id: s.id || s.uuid,
      uuid: s.uuid || s.id,
      title: s.title || s.topic || s.name || '',
      userName: s.userName || s.user_name || user.firstName || user.first_name || user.username || user.name || 'Unknown',
      userUsername: s.userUsername || s.user_username || user.username || '',
      userId: s.userId || s.user_id || user.id,
      date: s.date || s.created_at || s.createdAt || s.timestamp,
      userReaction: reaction,
      views: viewsTotal,
      viewsToday: viewsToday,
      platform: s.platform || 'bot',
      generationId: s.generationId ?? s.generation_id ?? null,
      generationStatus: s.generationStatus || s.generation_status || null,
      generationCostRub: s.generationCostRub ?? s.generation_cost_rub ?? null,
      generationCostUsd: s.generationCostUsd ?? s.generation_cost_usd ?? null
    };
  };

  const fetchSummaries = async (page) => {
    setTableLoading(true);
    try {
      const result = await API.stats.getRecentSummaries(page, itemsPerPage);
      console.log('fetchSummaries result:', result);
      if (result.success) {
        let data = result.data;
        
        // Handle potential { data, pagination } structure or other common patterns
        if (data && typeof data === 'object') {
          if (data.data && Array.isArray(data.data)) {
            if (data.pagination && typeof data.pagination.total !== 'undefined') {
              setTotalItems(data.pagination.total);
            }
            data = data.data;
          } else if (data.summaries && Array.isArray(data.summaries)) {
             if (data.total || data.count) setTotalItems(data.total || data.count);
             data = data.summaries;
          } else if (data.items && Array.isArray(data.items)) {
             if (data.total || data.count) setTotalItems(data.total || data.count);
             data = data.items;
          }
        }

        // Ensure we strictly respect itemsPerPage limit
        if (Array.isArray(data)) {
          const normalizedData = data.slice(0, itemsPerPage).map(normalizeSummary);
          setSummaries(normalizedData);
        } else {
          console.warn('Summaries data is not an array:', data);
          setSummaries([]);
        }
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error fetching summaries:', error);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      const [summariesRes, chartRes] = await Promise.all([
        API.stats.getRecentSummaries(1, itemsPerPage),
        API.stats.getSummariesChart()
      ]);
      
      if (summariesRes.success) {
        let data = summariesRes.data;
        console.log('Initial summaries result:', summariesRes);
        
        // Handle potential { data, pagination } structure or other common patterns
        if (data && typeof data === 'object') {
          if (data.data && Array.isArray(data.data)) {
            if (data.pagination && typeof data.pagination.total !== 'undefined') {
              setTotalItems(data.pagination.total);
            }
            data = data.data;
          } else if (data.summaries && Array.isArray(data.summaries)) {
             if (data.total || data.count) setTotalItems(data.total || data.count);
             data = data.summaries;
          } else if (data.items && Array.isArray(data.items)) {
             if (data.total || data.count) setTotalItems(data.total || data.count);
             data = data.items;
          }
        }

        if (Array.isArray(data)) {
          const normalizedData = data.slice(0, itemsPerPage).map(normalizeSummary);
          setSummaries(normalizedData);
        } else {
          console.warn('Initial summaries data is not an array:', data);
        }
      }

      if (chartRes.success) {
        console.log('Chart Data Loaded:', chartRes.data);
        setChartData(chartRes.data);
      } else {
        console.error('Chart Data Failed:', chartRes);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    chartColorsRef.current = getChartTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!stats || !lineChartRef.current || !chartData) return;

    const ctx = lineChartRef.current.getContext('2d');

    if (lineChartInstanceRef.current) {
      lineChartInstanceRef.current.destroy();
    }

    const data = chartData.values || [];
    const labels = chartData.labels || [];
    const colors = chartColorsRef.current;
    
    console.log('Rendering Chart with:', { labels, data });

    lineChartInstanceRef.current = new ChartJS(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Саммари сгенерировано',
            data: data,
            borderColor: colors.stroke,
            backgroundColor: colors.fill,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: colors.pointBg,
            pointBorderColor: colors.stroke,
            pointBorderWidth: 2,
            pointHoverRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: colors.tooltipBackground,
            padding: 12,
            titleFont: { size: 13, weight: 'bold' },
            bodyFont: { size: 12 },
            borderColor: colors.tooltipBorder,
            borderWidth: 1,
            titleColor: colors.tooltipText,
            bodyColor: colors.tooltipText,
            displayColors: false,
            callbacks: {
              label: (context) => {
                return `${context.parsed.y} саммари`;
              }
            },
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grace: '10%', // Add some breathing room at the top
            grid: { color: colors.grid, drawBorder: true },
            ticks: { 
              color: colors.ticks, 
              font: { size: 12 },
              precision: 0 // Ensure integer values
            }
          },
          x: {
            grid: { display: false },
            ticks: { color: colors.ticks, font: { size: 12 } }
          }
        }
      }
    });

    return () => {
      if (lineChartInstanceRef.current) {
        lineChartInstanceRef.current.destroy();
        lineChartInstanceRef.current = null;
      }
    };
  }, [stats, chartData, theme]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  // Use summaries directly as they are now paginated on server
  const paginatedSummaries = summaries || [];

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="tab-content">
        <div className="loading-state">
          <p>Не удалось загрузить данные статистики.</p>
        </div>
      </div>
    );
  }

  const summariesStats = stats.summaries || {};
  const viewsStats = summariesStats.views || {};

  return (
    <div className="tab-content">
      {/* Audio Duration Stats */}
      {audioDuration && <AudioDurationCard data={audioDuration} />}

      {/* Stats Grid */}
      <div className="summaries-stats-grid">
        <div className="stat-card">
          <div className="stat-label">Всего саммари</div>
          <div className="stat-value">{summariesStats.total ? summariesStats.total.toLocaleString('ru-RU') : 0}</div>
          <div className="stat-subtext">Сегодня: {summariesStats.today || 0}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Просмотры</div>
          <div className="stat-value">{viewsStats.total ? viewsStats.total.toLocaleString('ru-RU') : 0}</div>
          <div className="stat-subtext">
            {viewsStats.today > 0 ? (
              <span className="views-today">Сегодня: +{viewsStats.today.toLocaleString('ru-RU')}</span>
            ) : (
              'Нет просмотров сегодня'
            )}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <div className="chart-card">
          <h3>Тренд саммари за неделю</h3>
          {chartData ? (
            <canvas ref={lineChartRef}></canvas>
          ) : (
            <div className="empty-chart-message">Нет данных для отображения графика</div>
          )}
        </div>
      </div>

      {/* Recent Summaries Table */}
      <div className="recent-summaries-section">
        <h2>Последние саммари</h2>
        {tableLoading ? (
           <div className="loading-state" style={{ padding: '40px' }}>
             <div className="spinner"></div>
             <p>Загрузка саммари...</p>
           </div>
        ) : (
          <>
            <div className="summaries-table-wrapper">
              <table className="summaries-table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Платформа</th>
                    <th>Пользователь</th>
                    <th>Дата</th>
                    <th>Генерация</th>
                    <th>Просмотры</th>
                    <th>Ссылка</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSummaries.map(summary => (
                    <tr key={summary.id} className="summary-row">
                      <td>
                        <div className="summary-title">
                          {summary.title && summary.title.trim() !== '' ? summary.title : <span className="empty-title">Пустой</span>}
                        </div>
                      </td>
                      <td>
                        <span className={`platform-badge ${summary.platform === 'bot' ? 'platform-badge--bot' : ''}`}>
                          {summary.platform === 'bot' ? 'Bot' : summary.platform}
                        </span>
                      </td>
                      <td>
                        <button
                          className="user-link"
                          onClick={() => onUserClick(summary.userId)}
                        >
                          {summary.userName}
                        </button>
                      </td>
                      <td>
                        <div className="summary-date">
                          {formatDate(summary.date)}
                        </div>
                      </td>
                      <td>
                        <div className="generation-meta">
                          <div>#{summary.generationId || '—'}</div>
                          <div className={`generation-status generation-status-${summary.generationStatus || 'unknown'}`}>
                            {formatGenerationStatus(summary.generationStatus)}
                          </div>
                          <div>{formatNullableMoney(summary.generationCostRub, 'RUB')}</div>
                        </div>
                      </td>
                      <td>
                        <div className="summary-ratings">
                          <span className="views">
                            👁️ {summary.views}
                            {summary.viewsToday > 0 && (
                              <span className="views-today" title="Просмотры за сегодня">
                                (+{summary.viewsToday})
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td>
                        <a href={`${process.env.REACT_APP_SUMMARY_BASE_URL || 'https://earmiss.ru/#/summaries/'}${summary.uuid || summary.id}`} target="_blank" rel="noopener noreferrer" className="summary-link">
                          Смотреть →
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={fetchSummaries}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SummariesTab;
